import React, {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {toast} from "react-toastify";
import {confirmAlert} from "react-confirm-alert";
import PlatformSectionHeader from "./PlatformSectionHeader";
import {createManagedRegion, retrieveManagedRegions, updateManagedRegion} from "../../services/Platform/RegionAxios";

const PAGE_SIZE = 10;
const EMPTY_FORM = {code: "", name: "", parentCode: "", sortOrder: 0, activeYn: "Y"};

export default function RegionManage() {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [activeYn, setActiveYn] = useState("ALL");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [formMode, setFormMode] = useState(null);
  const [regionForm, setRegionForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [updatingStatusCode, setUpdatingStatusCode] = useState(null);

  const loadRegions = async city => {
    if (!city?.code) { setRegions([]); return; }
    setIsLoading(true);
    try {
      setRegions(await retrieveManagedRegions("REGION", city.code));
      setPage(1);
    } catch (error) {
      console.error("행정동 조회 실패:", error);
      toast.error(error.message || "행정동 정보를 불러오지 못했습니다.");
      setRegions([]);
    } finally { setIsLoading(false); }
  };

  const selectCity = async city => {
    setSelectedCity(city);
    setKeyword("");
    setActiveYn("ALL");
    await loadRegions(city);
  };

  const selectCountry = async country => {
    setSelectedCountry(country);
    setSelectedCity(null);
    setCities([]);
    setRegions([]);
    setKeyword("");
    setPage(1);
    try {
      const response = await retrieveManagedRegions("CITY", country.code);
      const activeCities = response.filter(city => city.activeYn === "Y");
      setCities(activeCities);
      if (activeCities[0]) await selectCity(activeCities[0]);
    } catch (error) {
      console.error("시·군·구 조회 실패:", error);
      toast.error(error.message || "시·군·구 정보를 불러오지 못했습니다.");
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const response = await retrieveManagedRegions("COUNTRY");
        const activeCountries = response.filter(country => country.activeYn === "Y");
        setCountries(activeCountries);
        if (activeCountries[0]) await selectCountry(activeCountries[0]);
      } catch (error) {
        console.error("시·도 조회 실패:", error);
        toast.error(error.message || "지역 정보를 불러오지 못했습니다.");
      } finally { setIsLoading(false); }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRegions = useMemo(() => {
    const search = keyword.trim().toLocaleLowerCase("ko");
    return regions.filter(region => {
      const keywordMatched = !search || String(region.name ?? "").toLocaleLowerCase("ko").includes(search) || String(region.code ?? "").includes(search);
      return keywordMatched && (activeYn === "ALL" || region.activeYn === activeYn);
    }).sort((first, second) =>
      String(first.code ?? "").localeCompare(
          String(second.code ?? ""),
          "ko",
          {numeric: true}
      )
    );
  }, [regions, keyword, activeYn]);
  const totalPages = Math.max(1, Math.ceil(filteredRegions.length / PAGE_SIZE));
  const pagedRegions = filteredRegions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const openCreate = () => {
    if (!selectedCity) { toast.warning("행정동을 추가할 시·군·구를 선택해주세요."); return; }
    setRegionForm({...EMPTY_FORM, parentCode: selectedCity.code, sortOrder: regions.length});
    setFormMode("create");
  };
  const openEdit = region => { setRegionForm({...EMPTY_FORM, ...region}); setFormMode("edit"); };
  const closeForm = () => { if (!isSaving) setFormMode(null); };

  const saveRegion = async () => {
    if (!/^\d{8}$/.test(regionForm.code)) { toast.warning("행정동 코드는 숫자 8자리로 입력해주세요."); return; }
    if (!regionForm.name.trim()) { toast.warning("행정동명을 입력해주세요."); return; }
    if (!Number.isInteger(Number(regionForm.sortOrder)) || Number(regionForm.sortOrder) < 0) { toast.warning("정렬 순서는 0 이상의 숫자로 입력해주세요."); return; }
    setIsSaving(true);
    try {
      if (formMode === "create") {
        await createManagedRegion(regionForm);
        toast.success("행정동이 추가되었습니다.");
      } else {
        await updateManagedRegion(regionForm.code, regionForm);
        toast.success("행정동 정보가 수정되었습니다.");
      }
      setFormMode(null);
      await loadRegions(selectedCity);
    } catch (error) {
      console.error("행정동 저장 실패:", error);
      toast.error(error.message || "행정동 정보를 저장하지 못했습니다.");
    } finally { setIsSaving(false); }
  };

  const toggleRegionStatus = async region => {
    if (updatingStatusCode) return;
    const nextActiveYn = region.activeYn === "Y" ? "N" : "Y";
    const nextStatusLabel = nextActiveYn === "Y" ? "활성화" : "비활성화";
    const confirmed = await new Promise(resolve => {
      confirmAlert({
        title: "행정동 " + nextStatusLabel,
        message: "‘" + region.name + "’ 행정동을 " + nextStatusLabel + "하시겠습니까?",
        buttons: [
          {
            label: nextStatusLabel,
            onClick: () => resolve(true)
          },
          {
            label: "취소",
            onClick: () => resolve(false)
          }
        ],
        closeOnEscape: true,
        closeOnClickOutside: true,
        onClickOutside: () => resolve(false),
        onKeypressEscape: () => resolve(false)
      });
    });
    if (!confirmed) return;

    setUpdatingStatusCode(region.code);
    try {
      await updateManagedRegion(region.code, {...region, activeYn: nextActiveYn});
      setRegions(current => current.map(item => item.code === region.code ? {...item, activeYn: nextActiveYn} : item));
      toast.success(`행정동이 ${nextActiveYn === "Y" ? "활성화" : "비활성화"}되었습니다.`);
    } catch (error) {
      console.error("행정동 상태 변경 실패:", error);
      toast.error(error.message || "행정동 상태를 변경하지 못했습니다.");
    } finally { setUpdatingStatusCode(null); }
  };

  const pageNumbers = Array.from({length: totalPages}, (_, index) => index + 1)
  .filter(value => value === 1 || value === totalPages || Math.abs(value - page) <= 1);

  return (
      <RegionPage>
        <HierarchyCard>
          <PlatformSectionHeader title="지역 선택" description="시·도와 시·군·구를 차례로 선택하여 관리할 행정동을 조회합니다." />
          <HierarchyBody>
            <MasterColumn>
              <ColumnHeader><div><strong>시·도</strong><span>{countries.length}개</span></div><small>지역을 선택해주세요.</small></ColumnHeader>
              <SelectionList>{countries.map(country => <SelectionButton key={country.code} type="button" $selected={selectedCountry?.code === country.code} onClick={() => selectCountry(country)}><RegionIdentity><span>{country.name}</span><Code>{country.code}</Code></RegionIdentity><RowMeta><Count>{country.childCount ?? 0}개</Count><Arrow>›</Arrow></RowMeta></SelectionButton>)}</SelectionList>
            </MasterColumn>
            <MasterColumn>
              <ColumnHeader><div><strong>시·군·구</strong><span>{cities.length}개</span></div><small>{selectedCountry ? `${selectedCountry.name} 하위 지역` : "시·도를 먼저 선택해주세요."}</small></ColumnHeader>
              <SelectionList>{cities.length > 0 ? cities.map(city => <SelectionButton key={city.code} type="button" $selected={selectedCity?.code === city.code} onClick={() => selectCity(city)}><RegionIdentity><span>{city.name}</span><Code>{city.code}</Code></RegionIdentity><RowMeta><Count>{city.childCount ?? 0}개</Count><Arrow>›</Arrow></RowMeta></SelectionButton>) : <ListEmpty>조회할 시·군·구가 없습니다.</ListEmpty>}</SelectionList>
            </MasterColumn>
            <SelectionSummary><SummaryIcon>⌖</SummaryIcon><small>현재 선택</small><strong>{selectedCountry?.name ?? "시·도 미선택"}</strong><span>{selectedCity?.name ?? "시·군·구 미선택"}</span><p>선택한 시·군·구에 포함된 행정동만 하단 목록에 표시됩니다.</p></SelectionSummary>
          </HierarchyBody>
        </HierarchyCard>

        <RegionCard>
          <PlatformSectionHeader title="행정동 목록" description={selectedCity ? `${selectedCountry?.name} ${selectedCity.name}에 등록된 행정동을 관리합니다.` : "시·군·구를 선택하면 행정동 목록이 표시됩니다."} count={filteredRegions.length} action={<AddButton type="button" onClick={openCreate} disabled={!selectedCity}>+ 행정동 추가</AddButton>} />
          <ListToolbar><SearchInput value={keyword} onChange={event => {setKeyword(event.target.value);setPage(1);}} placeholder="행정동명 또는 코드 검색" /><StatusSelect value={activeYn} onChange={event => {setActiveYn(event.target.value);setPage(1);}}><option value="ALL">전체 상태</option><option value="Y">활성</option><option value="N">비활성</option></StatusSelect></ListToolbar>
          <TableArea><ScrollGuide>Shift + 스크롤로 좌우
            이동</ScrollGuide><TableFrame><RegionTable>
            <thead>
            <tr>
              <th>상태</th>
              <th>행정동 코드</th>
              <th>행정동명</th>
              <th>시·도</th>
              <th>시·군·구</th>
              <th>기준일</th>
              <th>관리</th>
            </tr>
            </thead>
            <tbody>
            {isLoading ? <tr><EmptyCell colSpan="7">행정동 정보를 불러오고
              있습니다.</EmptyCell></tr> : pagedRegions.length === 0 ? <tr>
                  <EmptyCell colSpan="7">조회된 행정동이 없습니다.</EmptyCell></tr>
                : pagedRegions.map(region => <tr key={region.code}>
                  <td><StatusToggle type="button" role="switch"
                                    aria-checked={region.activeYn === "Y"}
                                    $active={region.activeYn === "Y"}
                                    disabled={updatingStatusCode
                                        === region.code}
                                    onClick={() => toggleRegionStatus(
                                        region)}><ToggleTrack
                      $active={region.activeYn === "Y"}><ToggleKnob
                      $active={region.activeYn
                          === "Y"}/></ToggleTrack><span>{updatingStatusCode
                  === region.code ? "변경 중" : region.activeYn === "Y" ? "활성"
                      : "비활성"}</span></StatusToggle></td>
                  <CodeCell>{region.code}</CodeCell><NameCell>{region.name}</NameCell>
                  <td>{selectedCountry?.name ?? "-"}</td>
                  <td>{selectedCity?.name ?? "-"}</td>
                  <td>{region.revisionDate || "-"}</td>
                  <td><RowActions><EditButton type="button"
                                              onClick={() => openEdit(
                                                  region)}>수정</EditButton></RowActions>
                  </td>
                </tr>)}
            </tbody>
          </RegionTable></TableFrame>
            {totalPages > 1 && <Pagination><PageButton type="button"
                                                       disabled={page === 1}
                                                       onClick={() => setPage(
                                                           value => value
                                                               - 1)}>‹</PageButton>{pageNumbers.map(
                (value, index) => <React.Fragment key={value}>{index > 0
                    && value - pageNumbers[index - 1] > 1 &&
                    <PageEllipsis>…</PageEllipsis>}<PageButton type="button"
                                                               $active={value
                                                                   === page}
                                                               onClick={() => setPage(
                                                                   value)}>{value}</PageButton></React.Fragment>)}<PageButton
                type="button" disabled={page === totalPages}
                onClick={() => setPage(
                    value => value + 1)}>›</PageButton></Pagination>}
          </TableArea>
        </RegionCard>

        {formMode && <ModalOverlay onMouseDown={event => event.target === event.currentTarget && closeForm()}><EditModal role="dialog" aria-modal="true"><ModalHeader><div><small>REGION SETTINGS</small><h2>{formMode === "create" ? "행정동 추가" : "행정동 수정"}</h2><p>{selectedCountry?.name} {selectedCity?.name}에 포함되는 행정동 정보입니다.</p></div><CloseButton type="button" onClick={closeForm}>×</CloseButton></ModalHeader><ModalBody><FormField><label>행정동 코드</label><input value={regionForm.code} maxLength="8" disabled={formMode === "edit"} onChange={event => setRegionForm(prev => ({...prev, code: event.target.value.replace(/\D/g, "")}))} placeholder="숫자 8자리" /></FormField><FormField><label>행정동명</label><input value={regionForm.name} onChange={event => setRegionForm(prev => ({...prev, name: event.target.value}))} /></FormField><ReadOnlyPath><span>상위 지역</span><strong>{selectedCountry?.name} › {selectedCity?.name}</strong></ReadOnlyPath><FormGrid><FormField><label>정렬 순서</label><input type="number" min="0" value={regionForm.sortOrder} onChange={event => setRegionForm(prev => ({...prev, sortOrder: event.target.value}))} /></FormField>{formMode === "edit" && <FormField><label>활성 상태</label><select value={regionForm.activeYn} onChange={event => setRegionForm(prev => ({...prev, activeYn: event.target.value}))}><option value="Y">활성</option><option value="N">비활성</option></select></FormField>}</FormGrid></ModalBody><ModalFooter><CancelButton type="button" onClick={closeForm}>취소</CancelButton><SaveButton type="button" onClick={saveRegion} disabled={isSaving}>{isSaving ? "저장 중" : formMode === "create" ? "추가" : "저장"}</SaveButton></ModalFooter></EditModal></ModalOverlay>}
      </RegionPage>
  );
}

const RegionPage=styled.div`display:flex;width:100%;flex-direction:column;gap:16px;`;
const HierarchyCard=styled.section`overflow:hidden;border:1px solid #e5e8eb;border-radius:10px;background:#fff;box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const RegionCard=styled(HierarchyCard)``;
const HierarchyBody=styled.div`display:grid;grid-template-columns:minmax(220px,1fr) minmax(260px,1.25fr) minmax(230px,.85fr);min-height:280px;padding:18px 20px 20px;gap:14px;background:#f7f8fa;@media(max-width:900px){grid-template-columns:1fr}`;
const MasterColumn=styled.div`overflow:hidden;border:1px solid #e1e5e8;border-radius:9px;background:#fff;`;
const ColumnHeader=styled.div`padding:14px 15px 12px;border-bottom:1px solid #e8ebed;background:#fff;div{display:flex;align-items:center;justify-content:space-between}strong{color:#2f353b;font-size:14px}span{color:#f5811f;font-size:13px;font-weight:800}small{display:block;margin-top:5px;color:#92989e;font-size:10px}`;
const SelectionList=styled.div`max-height:214px;overflow:auto;padding:6px;`;
const SelectionButton=styled.button`display:flex;width:100%;min-height:40px;align-items:center;justify-content:space-between;gap:10px;padding:0 10px;border:0;border-radius:6px;background:${({$selected})=>$selected?"#fff1e5":"transparent"};color:${({$selected})=>$selected?"#d96500":"#484f56"};font-size:12px;font-weight:${({$selected})=>$selected?800:600};text-align:left;cursor:pointer;&:hover{background:${({$selected})=>$selected?"#fff1e5":"#f7f8f9"}}`;
const RegionIdentity=styled.span`display:flex;min-width:0;align-items:center;gap:7px;>span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}`;
const Code=styled.small`color:#a0a6ac;font-family:Consolas,monospace;font-size:10px;`;
const RowMeta=styled.span`display:flex;flex:0 0 auto;align-items:center;gap:5px;`;
const Count=styled.small`min-width:28px;color:#9299a0;font-size:10px;text-align:right;`;
const Arrow=styled.span`color:#f5811f;font-size:18px;`;
const ListEmpty=styled.div`display:flex;min-height:150px;align-items:center;justify-content:center;color:#999;font-size:11px;`;
const SelectionSummary=styled.div`display:flex;align-items:center;justify-content:center;flex-direction:column;padding:22px;border:1px solid #eadfd6;border-radius:9px;background:linear-gradient(145deg,#fffaf5,#fff);text-align:center;small{margin-top:10px;color:#9a8d82;font-size:10px}strong{margin-top:7px;color:#353a40;font-size:17px}span{margin-top:5px;color:#f5811f;font-size:14px;font-weight:800}p{max-width:210px;margin:16px 0 0;color:#8b9299;font-size:11px;line-height:1.6}`;
const SummaryIcon=styled.div`display:flex;width:42px;height:42px;align-items:center;justify-content:center;border-radius:50%;background:#fff0e2;color:#f5811f;font-size:22px;`;
const AddButton=styled.button`height:36px;padding:0 15px;border:1px solid #f5811f;border-radius:7px;background:#f5811f;color:#fff;font-size:12px;font-weight:800;cursor:pointer;&:disabled{border-color:#d7dade;background:#d7dade;cursor:not-allowed}`;
const ListToolbar=styled.div`display:flex;justify-content:flex-end;gap:8px;padding:14px 20px 0;background:#fff;`;
const SearchInput=styled.input`width:250px;height:38px;box-sizing:border-box;padding:0 12px;border:1px solid #dfe3e6;border-radius:7px;outline:0;font-size:12px;&:focus{border-color:#f5811f;box-shadow:0 0 0 3px rgba(245,129,31,.1)}`;
const StatusSelect=styled.select`width:112px;height:38px;padding:0 10px;border:1px solid #dfe3e6;border-radius:7px;background:#fff;color:#555;font-size:12px;`;
const TableArea=styled.div`padding:9px 20px 20px;`;
const ScrollGuide=styled.div`margin-bottom:7px;color:#969da5;font-size:10px;text-align:right;`;
const TableFrame=styled.div`overflow:auto;border:1px solid #dfe3e6;border-radius:8px;`;
const RegionTable=styled.table`width:100%;min-width:930px;border-collapse:collapse;th{height:42px;padding:0 12px;border-bottom:1px solid #dfe3e6;background:#f7f8fa;color:#616971;font-size:13px;font-weight:800;white-space:nowrap}td{height:45px;padding:0 12px;border-bottom:1px solid #edf0f2;color:#444;font-size:13px;text-align:center}tbody tr:last-child td{border-bottom:0}tbody tr:hover td{background:#fffaf6}`;
const EmptyCell=styled.td`height:155px!important;color:#999!important;`;
const NameCell=styled.td`font-weight:800;`;
const CodeCell=styled.td`color:#626a72!important;font-family:Consolas,monospace;`;
const StatusToggle=styled.button`display:inline-flex;min-width:82px;height:28px;align-items:center;justify-content:center;gap:7px;padding:0 8px;border:1px solid ${({$active})=>$active?"#bde7cc":"#dfe2e5"};border-radius:14px;background:${({$active})=>$active?"#edf9f1":"#f4f5f6"};color:${({$active})=>$active?"#219653":"#7f878e"};font-size:10px;font-weight:800;cursor:pointer;&:disabled{cursor:wait;opacity:.65}`;
const ToggleTrack=styled.span`position:relative;width:25px;height:14px;flex:0 0 25px;border-radius:7px;background:${({$active})=>$active?"#31ad65":"#b6bcc1"};transition:background .16s ease;`;
const ToggleKnob=styled.span`position:absolute;top:2px;left:${({$active})=>$active?"13px":"2px"};width:10px;height:10px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.2);transition:left .16s ease;`;
const RowActions=styled.div`display:flex;justify-content:center;gap:5px;`;
const EditButton=styled.button`height:28px;padding:0 10px;border:1px solid #f1c7aa;border-radius:6px;background:#fff7f0;color:#df6d12;font-size:10px;font-weight:800;cursor:pointer;`;
const Pagination=styled.div`display:flex;align-items:center;justify-content:center;gap:5px;margin-top:15px;`;
const PageButton=styled.button`min-width:30px;height:30px;border:1px solid ${({$active})=>$active?"#f5811f":"#dfe3e6"};border-radius:6px;background:${({$active})=>$active?"#f5811f":"#fff"};color:${({$active})=>$active?"#fff":"#666"};font-size:11px;font-weight:700;cursor:pointer;&:disabled{cursor:not-allowed;opacity:.4}`;
const PageEllipsis=styled.span`padding:0 3px;color:#999;`;
const ModalOverlay=styled.div`position:fixed;z-index:10000;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(20,27,34,.5);`;
const EditModal=styled.div`width:min(510px,100%);overflow:hidden;border-radius:14px;background:#fff;box-shadow:0 24px 70px rgba(0,0,0,.24);`;
const ModalHeader=styled.header`display:flex;justify-content:space-between;padding:22px 24px 18px;border-bottom:1px solid #f0e5dc;background:linear-gradient(135deg,#fff,#fff7ef);small{color:#f5811f;font-size:9px;font-weight:800;letter-spacing:.12em}h2{margin:5px 0;color:#262c32;font-size:19px}p{margin:0;color:#818991;font-size:11px}`;
const CloseButton=styled.button`width:32px;height:32px;border:0;border-radius:7px;background:transparent;color:#777;font-size:24px;cursor:pointer;`;
const ModalBody=styled.div`display:flex;flex-direction:column;gap:14px;padding:22px 24px;`;
const FormGrid=styled.div`display:grid;grid-template-columns:1fr 1fr;gap:12px;`;
const FormField=styled.div`display:flex;flex-direction:column;gap:7px;label{color:#555;font-size:11px;font-weight:800}input,select{width:100%;height:40px;box-sizing:border-box;padding:0 11px;border:1px solid #dfe3e6;border-radius:7px;background:#fff;color:#333;font-size:12px;outline:0}input:focus,select:focus{border-color:#f5811f;box-shadow:0 0 0 3px rgba(245,129,31,.1)}input:disabled{background:#f4f5f6;color:#888}`;
const ReadOnlyPath=styled.div`display:flex;align-items:center;justify-content:space-between;padding:12px 13px;border:1px solid #ebeef0;border-radius:7px;background:#f8f9fa;span{color:#888;font-size:11px}strong{color:#555;font-size:12px}`;
const ModalFooter=styled.footer`display:flex;justify-content:flex-end;gap:8px;padding:14px 24px;border-top:1px solid #e9ecef;background:#f8f9fa;`;
const CancelButton=styled.button`min-width:76px;height:38px;border:1px solid #dfe3e7;border-radius:7px;background:#fff;color:#666;font-weight:700;cursor:pointer;`;
const SaveButton=styled.button`min-width:76px;height:38px;border:1px solid #f5811f;border-radius:7px;background:#f5811f;color:#fff;font-weight:700;cursor:pointer;&:disabled{opacity:.55}`;
